import { Autocomplete, Box, Button, Chip, FormControl, InputLabel, MenuItem, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { NumericFormat } from "react-number-format";
import {
    setDescription,
    setDuration,
    setDurationType,
    setJobType,
    setLevel,
    setPayAmount,
    setPayAmountMax,
    setPayRate,
    setPayType,
    setPrimaryLanguage,
    setQuestions,
    setResponsibilities,
    setSkills,
    setSpecialization,
    setTags,
    setTitle,
    setWorkSite,
    setDeliverables,
} from "../../../Redux/slices/JobPostCreationSlice";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { KaziModal } from "../../../Shared Components/Kazi/KaziModal";
import { TalentSpecializations, languages } from "../../../Types/General";
import { DurationType, JobType, PaymentType, WorkSite } from "../../../Types/Job";

interface EditSectionModalProps {
  open: boolean;
  onClose: () => void;
  section: string;
}

export const EditSectionModal = ({ open, onClose, section }: EditSectionModalProps) => {
  const dispatch = useAppDispatch();
  const {
    title,
    level,
    tags,
    description,
    responsibilities,
    skills,
    payType,
    payAmount,
    payAmountMax,
    jobType,
    payRate,
    duration,
    durationType,
    primaryLanguage,
    questions,
    workSite,
    specialization,
    deliverables,
  } = useAppSelector((state) => state.jobPostCreation);

  const [localTitle, setLocalTitle] = useState(title);
  const [localLevel, setLocalLevel] = useState(level);
  const [localTags, setLocalTags] = useState(tags);
  const [localDescription, setLocalDescription] = useState(description);
  const [localResponsibilities, setLocalResponsibilities] = useState(responsibilities.join("\n"));
  const [localSkills, setLocalSkills] = useState(skills.join("\n"));
  const [localPayType, setLocalPayType] = useState(payType);
  const [localPayAmount, setLocalPayAmount] = useState(payAmount);
  const [localPayAmountMax, setLocalPayAmountMax] = useState(payAmountMax);
  const [localJobType, setLocalJobType] = useState(jobType);
  const [localPayRate, setLocalPayRate] = useState(payRate);
  const [localDuration, setLocalDuration] = useState(duration);
  const [localDurationType, setLocalDurationType] = useState(durationType);
  const [localPrimaryLanguage, setLocalPrimaryLanguage] = useState(primaryLanguage);
  const [localQuestions, setLocalQuestions] = useState(questions.join("\n"));
  const [localWorkSite, setLocalWorkSite] = useState(workSite);
  const [localSpecialization, setLocalSpecialization] = useState(specialization);
  const [localDeliverables, setLocalDeliverables] = useState(deliverables.join("\n"));

  const handleSave = () => {
    switch (section) {
      case "title":
        dispatch(setTitle(localTitle));
        dispatch(setLevel(localLevel));
        dispatch(setTags(localTags));
        break;
      case "description":
        dispatch(setDescription(localDescription));
        dispatch(setResponsibilities(localResponsibilities.split("\n").filter(r => r.trim())));
        dispatch(setSkills(localSkills.split("\n").filter(skill => skill.trim())));
        break;
      case "payType":
        // dispatch(setPayType(localPayType));
        dispatch(setPayAmount(localPayAmount));
        dispatch(setPayAmountMax(localPayAmountMax));
        dispatch(setJobType(localJobType));
        dispatch(setPayRate(localPayRate));
        dispatch(setDuration(localDuration));
        dispatch(setDurationType(localDurationType));
        break;
      case "additional":
        dispatch(setPrimaryLanguage(localPrimaryLanguage));
        dispatch(setQuestions(localQuestions.split("\n").filter(q => q.trim())));
        dispatch(setWorkSite(localWorkSite));
        dispatch(setSpecialization(localSpecialization));
        break;
      case "deliverables":
        dispatch(setDeliverables(localDeliverables.split("\n").filter(d => d.trim())));
        break;
    }
    onClose();
  };

  const getContent = () => {
    switch (section) {
      case "title":
        return (
          <Box>
            <TextField
              fullWidth
              label="Job Title"
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <TextField
                select
                value={localLevel}
                onChange={(e) => setLocalLevel(e.target.value)}
                label="Level"
              >
                {[
                  "Entry-level ( 0-2 years)", 
                  "Mid-level (3-5 years)", 
                  "Senior-level (6+ years)",
                  "Flexible"
                ].map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <Box>
              <Typography variant="body-medium" color="black.300" mb={1}>
                Tags
              </Typography>
              <Autocomplete
                multiple
                freeSolo
                autoSelect
                options={TalentSpecializations.map(s => s.filters).flat().sort()}
                value={localTags}
                onChange={(_, newValue) => setLocalTags(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={localTags.length === 0 ? "Type in a tag and press enter" : "Add more tags..."}
                  />
                )}
              />
            </Box>
          </Box>
        );
      case "description":
        return (
          <Box>
            <TextField
              fullWidth
              label="Job Description"
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              margin="normal"
              multiline
              rows={4}
            />
            <TextField
              fullWidth
              label="Responsibilities (one per line)"
              value={localResponsibilities}
              onChange={(e) => setLocalResponsibilities(e.target.value)}
              margin="normal"
              multiline
              rows={4}
              helperText="Enter each responsibility on a new line"
            />
            <TextField
              fullWidth
              label="Qualifications (one per line)"
              value={localSkills}
              onChange={(e) => setLocalSkills(e.target.value)}
              margin="normal"
              multiline
              rows={4}
              helperText="Enter each qualification on a new line"
            />
          </Box>
        );
      case "payType":
        return (
          <Box>
            {/* <FormControl fullWidth margin="normal">
              <TextField
                select
                value={localPayType}
                onChange={(e) => setLocalPayType(e.target.value)}
                label="Pay Type"
              >
                <MenuItem value="Fixed">Fixed</MenuItem>
                <MenuItem value="Range">Range</MenuItem>
              </TextField>
            </FormControl> */}
            <FormControl fullWidth margin="normal">
              <TextField
                select
                value={localJobType}
                onChange={(e) => setLocalJobType(e.target.value)}
                label="Job Type"
              >
                {JobType.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <TextField
                select
                value={localPayRate}
                onChange={(e) => setLocalPayRate(e.target.value)}
                label="Pay Rate"
              >
                {PaymentType.map((rate) => (
                  <MenuItem key={rate} value={rate}>
                    {rate}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            {localJobType === "Internship" && (
              <Box display="flex" gap={2} mt={2}>
                <TextField
                  label="Duration"
                  value={localDuration}
                  onChange={(e) => setLocalDuration(e.target.value)}
                  type="number"
                  fullWidth
                />
                <FormControl fullWidth>
                  <TextField
                    select
                    value={localDurationType}
                    onChange={(e) => setLocalDurationType(e.target.value)}
                    label="Unit"
                  >
                    {DurationType.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              </Box>
            )}
            <Box display="flex" gap={2} mt={2}>
              <NumericFormat
                customInput={TextField}
                label="Minimum Amount"
                value={localPayAmount}
                onValueChange={(values) => {
                  setLocalPayAmount(values.value);
                }}
                thousandSeparator=","
                prefix="$ "
                decimalScale={0}
                allowNegative={false}
                fullWidth
              />
              {localPayType === "Range" && (
                <NumericFormat
                  customInput={TextField}
                  label="Maximum Amount"
                  value={localPayAmountMax}
                  onValueChange={(values) => {
                    setLocalPayAmountMax(values.value);
                  }}
                  thousandSeparator=","
                  prefix="$ "
                  decimalScale={0}
                  allowNegative={false}
                  fullWidth
                />
              )}
            </Box>
          </Box>
        );
      case "additional":
        return (
          <Box>
            <FormControl fullWidth margin="normal">
              <TextField
                select
                value={localPrimaryLanguage}
                onChange={(e) => setLocalPrimaryLanguage(e.target.value)}
                label="Primary Language"
              >
                {languages.map((language) => (
                  <MenuItem key={language} value={language}>
                    {language}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <TextField
              fullWidth
              label="Screening Questions (one per line)"
              value={localQuestions}
              onChange={(e) => setLocalQuestions(e.target.value)}
              margin="normal"
              multiline
              rows={4}
              helperText="Enter each question on a new line"
            />
            <FormControl fullWidth margin="normal">
              <TextField
                select
                value={localWorkSite}
                onChange={(e) => setLocalWorkSite(e.target.value)}
                label="Work Site"
              >
                {WorkSite.map((site) => (
                  <MenuItem key={site} value={site}>
                    {site}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <TextField
                select
                value={localSpecialization}
                onChange={(e) => setLocalSpecialization(e.target.value)}
                label="Specialization"
              >
                {TalentSpecializations.map((spec) => (
                  <MenuItem key={spec.label} value={spec.label}>
                    {spec.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Box>
        );
      case "deliverables":
        return (
          <Box>
            <TextField
              fullWidth
              label="Deliverables (one per line)"
              value={localDeliverables}
              onChange={(e) => setLocalDeliverables(e.target.value)}
              margin="normal"
              multiline
              rows={4}
              helperText="Enter each deliverable on a new line"
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <KaziModal
      open={open}
      handleClose={onClose}
      title={`Edit ${section.charAt(0).toUpperCase() + section.slice(1)}`}
      content={getContent()}
      actions={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" onClick={handleSave} variant="contained">Save</Button>
      ]}
    />
  );
};

export default EditSectionModal; 